import { IMaterial } from '../models';
import { parseServerDate } from '../utils/serverDate';

/*
 * MMG-4461: the "changes since <date>" catalog download arrives in ID order and used to save its
 * marker only after the last item, so a page that died mid-way restarted the whole catch-up.
 * Storing oldest-first and moving the marker after every group means each attempt asks for less.
 */

const timeOf = (material: IMaterial): number => {
    const t = parseServerDate(material.date);
    return Number.isFinite(t) ? t : -1; // undated items sort first and never move the marker
};

// Oldest first; undated items lead so they are stored before the marker starts moving
export const sortMaterialsOldestFirst = (materials: IMaterial[]): IMaterial[] =>
    materials
        .map((material, index) => ({ material, t: timeOf(material), index }))
        .sort((a, b) => a.t - b.t || a.index - b.index)
        .map(entry => entry.material);

/*
 * The marker to save once items [0, storedCount) of a sorted list are in the store, or null to leave it alone.
 * Only advances to the last stored item's time when the next unstored item is strictly newer, so a group
 * boundary inside a run of identical timestamps cannot skip the rest of that run. Never moves backwards.
 */
export const markerAfterStoring = (sorted: IMaterial[], storedCount: number, previousMarker: Date | null): Date | null => {
    if (storedCount <= 0 || storedCount > sorted.length) return null;
    const last = sorted[storedCount - 1];
    if (!last) return null;
    const lastTime = timeOf(last);
    if (lastTime < 0) return null;

    const next = sorted[storedCount];
    if (next && timeOf(next) <= lastTime) return null;

    if (previousMarker && previousMarker.getTime() >= lastTime) return null;
    return new Date(lastTime);
};
